import { AddDialog } from '@/components/AddDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LinkSelectDialog, UnlinkItemsDialog } from '@/ui/composites/entityLinking';
import { Text, MenuItem } from '@/ui/primitives';
import { Select } from '@/ui/primitives/Select';
import { TextField } from '@/ui/primitives/TextField';
import type { FieldErrorProps } from '@/ui/hooks/useFormErrors';
import type { FeatureDto, ReleaseListItemDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type ProjectOption = { id: string; name: string };

type SuiteFormValues = { name: string; projectId: string };

type SuiteFormHandlers = {
  onNameChange: (value: string) => void;
  onProjectChange?: (value: string) => void;
};

type AddSuiteDialogProps = {
  open: boolean;
  title: string;
  saving?: boolean;
  values: SuiteFormValues;
  errors?: { name?: FieldErrorProps };
  projectOptions: ProjectOption[];
  showProjectSelect: boolean;
  contextText?: string;
  errorMessage?: string | null;
  onClose: () => void;
  onSave: () => void;
  handlers: SuiteFormHandlers;
};

export const AddSuiteDialog = ({
  open,
  title,
  saving = false,
  values,
  errors,
  projectOptions,
  showProjectSelect,
  contextText,
  errorMessage,
  onClose,
  onSave,
  handlers,
}: AddSuiteDialogProps): ReactElement => {
  const Field = TextField;
  return (
    <AddDialog
      open={open}
      title={title}
      saving={saving}
      description={contextText}
      errorMessage={errorMessage}
      onClose={onClose}
      onSave={onSave}
    >
      <Field
        label="Name"
        value={values.name}
        onChange={(event) => {
          handlers.onNameChange(event.target.value);
        }}
        required
        autoFocus
        {...(errors?.name ?? {})}
      />
      {showProjectSelect ? (
        <Select
          required
          label="Project"
          value={values.projectId}
          onChange={(event) => {
            handlers.onProjectChange?.(event.target.value as string);
          }}
        >
          {projectOptions.map((project) => (
            <MenuItem key={project.id} value={project.id}>
              {project.name}
            </MenuItem>
          ))}
        </Select>
      ) : null}
    </AddDialog>
  );
};

type EditSuiteDialogProps = {
  open: boolean;
  saving?: boolean;
  values: SuiteFormValues;
  errors?: { name?: FieldErrorProps };
  errorMessage?: string | null;
  onClose: () => void;
  onSave: () => void;
  handlers: SuiteFormHandlers;
};

export const EditSuiteDialog = ({
  open,
  saving = false,
  values,
  errors,
  errorMessage,
  onClose,
  onSave,
  handlers,
}: EditSuiteDialogProps): ReactElement => {
  const Field = TextField;
  return (
    <AddDialog
      open={open}
      title="Edit Suite"
      saving={saving}
      errorMessage={errorMessage}
      onClose={onClose}
      onSave={onSave}
    >
      <Field
        label="Name"
        value={values.name}
        onChange={(event) => {
          handlers.onNameChange(event.target.value);
        }}
        required
        autoFocus
        {...(errors?.name ?? {})}
      />
      <Text role="body" color="textSecondary">
        Updating the suite type will update the type on all tests linked to this suite.
      </Text>
    </AddDialog>
  );
};

type DeleteSuiteDialogProps = {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const DeleteSuiteDialog = ({
  open,
  loading = false,
  onClose,
  onConfirm,
}: DeleteSuiteDialogProps): ReactElement => {
  return (
    <ConfirmDialog
      open={open}
      title="Delete Suite"
      description="This suite will be deleted."
      confirmLabel="Confirm"
      confirmColor="red"
      loading={loading}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
};

type LinkFeatureToSuiteDialogProps = {
  open: boolean;
  saving?: boolean;
  value: string;
  featureOptions: FeatureDto[];
  errorMessage?: string | null;
  fieldError?: string;
  onClose: () => void;
  onSave: () => void;
  onFeatureChange: (value: string) => void;
};

export const LinkFeatureToSuiteDialog = ({
  open,
  saving = false,
  value,
  featureOptions,
  errorMessage,
  fieldError,
  onClose,
  onSave,
  onFeatureChange,
}: LinkFeatureToSuiteDialogProps): ReactElement => {
  return (
    <LinkSelectDialog
      open={open}
      title="Link Feature To Suite"
      saving={saving}
      label="Feature"
      value={value}
      options={featureOptions}
      fieldError={fieldError}
      errorMessage={errorMessage}
      description="The selected feature will be linked to this suite."
      onClose={onClose}
      onSave={onSave}
      onChange={onFeatureChange}
    />
  );
};

type UnlinkFeaturesFromSuiteDialogProps = {
  open: boolean;
  saving?: boolean;
  featureOptions: FeatureDto[];
  selectedFeatureIds: string[];
  errorMessage?: string | null;
  disableSave?: boolean;
  onClose: () => void;
  onSave: () => void;
  onToggleFeature: (featureId: string) => void;
};

export const UnlinkFeaturesFromSuiteDialog = ({
  open,
  saving = false,
  featureOptions,
  selectedFeatureIds,
  errorMessage,
  disableSave = false,
  onClose,
  onSave,
  onToggleFeature,
}: UnlinkFeaturesFromSuiteDialogProps): ReactElement => {
  return (
    <UnlinkItemsDialog
      open={open}
      title="Unlink Features From Suite"
      saving={saving}
      items={featureOptions}
      selectedIds={selectedFeatureIds}
      itemLabel="Features"
      errorMessage={errorMessage}
      disableSave={disableSave}
      description="Selected features will be unlinked from this suite."
      onClose={onClose}
      onSave={onSave}
      onToggle={onToggleFeature}
    />
  );
};

type AddFeatureToSuiteDialogProps = {
  open: boolean;
  saving?: boolean;
  values: { name: string; status: string };
  errors?: { name?: FieldErrorProps };
  statusOptions: Array<{ value: FeatureDto['status']; label: string }>;
  errorMessage?: string | null;
  onClose: () => void;
  onSave: () => void;
  onNameChange: (value: string) => void;
  onStatusChange: (value: string) => void;
};

type LinkReleaseToSuiteDialogProps = {
  open: boolean;
  saving?: boolean;
  value: string;
  releaseOptions: ReleaseListItemDto[];
  errorMessage?: string | null;
  fieldError?: string;
  onClose: () => void;
  onSave: () => void;
  onReleaseChange: (value: string) => void;
};

export const LinkReleaseToSuiteDialog = ({
  open,
  saving = false,
  value,
  releaseOptions,
  errorMessage,
  fieldError,
  onClose,
  onSave,
  onReleaseChange,
}: LinkReleaseToSuiteDialogProps): ReactElement => {
  return (
    <LinkSelectDialog
      open={open}
      title="Link Release To Suite"
      saving={saving}
      label="Release"
      value={value}
      options={releaseOptions}
      fieldError={fieldError}
      errorMessage={errorMessage}
      description="This release will be linked to this suite."
      onClose={onClose}
      onSave={onSave}
      onChange={onReleaseChange}
    />
  );
};

type UnlinkReleasesFromSuiteDialogProps = {
  open: boolean;
  saving?: boolean;
  releaseOptions: ReleaseListItemDto[];
  selectedReleaseIds: string[];
  errorMessage?: string | null;
  disableSave?: boolean;
  onClose: () => void;
  onSave: () => void;
  onToggleRelease: (releaseId: string) => void;
};

export const UnlinkReleasesFromSuiteDialog = ({
  open,
  saving = false,
  releaseOptions,
  selectedReleaseIds,
  errorMessage,
  disableSave = false,
  onClose,
  onSave,
  onToggleRelease,
}: UnlinkReleasesFromSuiteDialogProps): ReactElement => {
  return (
    <UnlinkItemsDialog
      open={open}
      title="Unlink Releases From Suite"
      saving={saving}
      items={releaseOptions}
      selectedIds={selectedReleaseIds}
      itemLabel="Releases"
      errorMessage={errorMessage}
      disableSave={disableSave}
      description="Remove direct release links for this suite."
      onClose={onClose}
      onSave={onSave}
      onToggle={onToggleRelease}
    />
  );
};

export const AddFeatureToSuiteDialog = ({
  open,
  saving = false,
  values,
  errors,
  statusOptions,
  errorMessage,
  onClose,
  onSave,
  onNameChange,
  onStatusChange,
}: AddFeatureToSuiteDialogProps): ReactElement => {
  const Field = TextField;
  return (
    <AddDialog
      open={open}
      title="Add Feature To Suite"
      saving={saving}
      errorMessage={errorMessage}
      onClose={onClose}
      onSave={onSave}
    >
      <Field
        label="Name"
        value={values.name}
        onChange={(event) => {
          onNameChange(event.target.value);
        }}
        required
        autoFocus
        {...(errors?.name ?? {})}
      />
      <Select
        label="Status"
        value={values.status}
        onChange={(event) => {
          onStatusChange(event.target.value as FeatureDto['status']);
        }}
      >
        {statusOptions.map((status) => (
          <MenuItem key={status.value} value={status.value}>
            {status.label}
          </MenuItem>
        ))}
      </Select>
    </AddDialog>
  );
};
